import React, { useRef, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import { AlertCircle } from 'lucide-react';

interface DroppableInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  label: string;
  required?: boolean;
  type?: 'text' | 'textarea';
  rows?: number;
}

export default function DroppableInput({
  value,
  onChange,
  placeholder,
  error,
  label,
  required = false,
  type = 'text',
  rows = 4,
}: DroppableInputProps) {
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  const [{ isOver, canDrop }, drop] = useDrop(() => ({
    accept: 'VARIABLE',
    drop: (item: { name: string; label: string }, monitor) => {
      const variable = `{{${item.name}}}`;

      // Get the current cursor position
      const input = inputRef.current;
      if (input) {
        const cursorPosition = input.selectionStart || value.length;
        const newValue =
          value.substring(0, cursorPosition) +
          variable +
          value.substring(cursorPosition);
        onChange(newValue);

        // Set cursor after the inserted variable
        setTimeout(() => {
          input.focus();
          const newPosition = cursorPosition + variable.length;
          input.setSelectionRange(newPosition, newPosition);
        }, 0);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  }));

  const borderClass = isOver && canDrop
    ? 'border-brand-blue-500 ring-2 ring-brand-blue-200'
    : error
    ? 'border-red-500'
    : 'border-gray-300';

  const bgClass = isOver && canDrop ? 'bg-brand-blue-50' : 'bg-white';

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div ref={drop}>
        {type === 'text' ? (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm transition-all ${borderClass} ${bgClass}`}
          />
        ) : (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm transition-all resize-none ${borderClass} ${bgClass}`}
          />
        )}
      </div>
      {error && (
        <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
          <AlertCircle className="w-4 h-4" />
          {error}
        </p>
      )}
      {isOver && canDrop && (
        <p className="text-xs text-brand-blue-600 mt-1">Drop to insert variable</p>
      )}
      {!error && !isOver && (
        <p className="text-xs text-gray-500 mt-1">
          {type === 'text'
            ? `Use {{variable_name}} syntax or drag variables from the panel`
            : `Drag variables here or type {{variable_name}}`}
        </p>
      )}
    </div>
  );
}
